import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../apiConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, faPhoneAlt, faCalendarAlt, 
    faUnlock, faBan, faUsers, faUserCheck, faIdBadge,
    faUserSlash, faExternalLinkAlt, faWallet, faEdit, faCheckDouble, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';

const AdminUsers = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);

    const getHeaders = useCallback(() => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }), []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/api/users`, { headers: getHeaders() });
            const result = await res.json();
            setUsers(Array.isArray(result) ? result : (result.data || []));
        } catch (e) {
            console.error("Fetch Error:", e);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleToggleBlock = async (id, currentStatus) => {
        const newStatus = currentStatus === 1 ? 0 : 1;
        if (window.confirm(newStatus === 1 ? t('confirm_block_user') : t('confirm_unblock_user'))) {
            try {
                const res = await fetch(`${API_BASE}/api/users/toggle-block/${id}`, {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({ is_blocked: newStatus })
                });
                if (res.ok) fetchUsers();
            } catch (e) { console.error(e); }
        }
    };

    const handleEditBalance = async (id, currentBalance) => {
        const newBalance = window.prompt(t('prompt_new_balance'), currentBalance);
        if (newBalance !== null && !isNaN(newBalance)) {
            try {
                const res = await fetch(`${API_BASE}/api/users/update-balance/${id}`, {
                    method: 'POST',
                    headers: getHeaders(),
                    body: JSON.stringify({ balance: parseFloat(newBalance) })
                });
                if (res.ok) fetchUsers();
            } catch (e) { console.error(e); }
        }
    };

    const handleToggleByPay = async (id, currentStatus) => {
        try {
            const res = await fetch(`${API_BASE}/api/users/toggle-bypay/${id}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ can_pay_later: currentStatus === 1 ? 0 : 1 })
            });
            if (res.ok) fetchUsers();
        } catch (e) { console.error(e); }
    };

    const filtered = users.filter(u => 
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.phone?.includes(searchTerm) ||
        u.id?.toString().includes(searchTerm)
    );

    const stats = {
        total: users.length,
        active: users.filter(u => u.is_blocked !== 1).length,
        blocked: users.filter(u => u.is_blocked === 1).length,
        totalBalance: users.reduce((sum, u) => sum + (Number(u.balance) || 0), 0)
    };

    return (
        <div className="users-page-container">
            <style>{modernUserStyles}</style>
            
            <div className="users-header-section">
                <div className="header-info">
                    <h1>{t('users_center_title')}</h1>
                    <p>{t('users_center_subtitle')}</p>
                </div>
                <div className="stats-grid">
                    <StatBox icon={faUsers} label={t('stat_total')} value={stats.total} className="stat-blue" />
                    <StatBox icon={faWallet} label={t('stat_total_balance')} value={`${stats.totalBalance.toLocaleString()} ֏`} className="stat-gold" />
                    <StatBox icon={faUserCheck} label={t('stat_active')} value={stats.active} className="stat-green" />
                    <StatBox icon={faUserSlash} label={t('stat_blocked')} value={stats.blocked} className="stat-red" />
                </div>
            </div>

            <div className="users-toolbar">
                <div className="search-wrapper">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input 
                        type="text" 
                        placeholder={t('search_placeholder')} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                </div>
                <button className={`refresh-action-btn ${loading ? 'spinning' : ''}`} onClick={fetchUsers}>
                    <FontAwesomeIcon icon={faSyncAlt} /> {loading ? t('loading') : t('btn_refresh_list')}
                </button>
            </div>

            <div className="users-table-card">
                <table className="modern-users-table">
                    <thead>
                        <tr>
                            <th>{t('col_user')}</th>
                            <th>{t('col_contacts')}</th>
                            <th>{t('col_balance')}</th>
                            <th>{t('col_date')}</th>
                            <th>{t('col_status')}</th>
                            <th>{t('col_actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length > 0 ? filtered.map(u => (
                            <tr key={u.id} className={u.is_blocked ? 'row-blocked' : ''}>
                                <td data-label={t('col_user')}>
                                    <div className="user-profile-cell">
                                        <div className="avatar-wrapper">
                                           
{u.profile_pic ? 
   <img 
    src={u.profile_pic ? `${API_BASE.replace(/\/$/, '')}/${u.profile_pic.replace(/^\//, '')}` : '/assets/img/default-avatar.png'} 
    alt={u.name || "User profile"} 
    loading="lazy"      // Բեռնում է միայն այն ժամանակ, երբ տեսանելի է
    decoding="async"    // Նկարի մշակումը թողնում է ֆոնային ռեժիմում
    onError={(e) => { 
        e.target.onerror = null; 
        e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png'; 
    }} 
/>: 
    <div className="avatar-initials">{u.full_name?.[0] || 'U'}</div>
}
                                        </div>
                                        <div className="user-info-text">
                                            <span className="full-name">{u.full_name}</span>
                                            <span className="user-id"><FontAwesomeIcon icon={faIdBadge} /> #{u.id}</span>
                                        </div>
                                    </div>
                                </td>
                                <td data-label={t('col_contacts')}>
                                    <div className="contact-info">
                                        <span className="primary-phone"><FontAwesomeIcon icon={faPhoneAlt} /> {u.phone}</span>
                                        {u.phone1 && <span className="secondary-phone">{u.phone1}</span>}
                                    </div>
                                </td>
                                <td data-label={t('col_balance')}>
                                    <div className="balance-info">
                                        <span className="amount">{Number(u.balance || 0).toLocaleString()} ֏</span>
                                        <button className="edit-balance-trigger" onClick={() => handleEditBalance(u.id, u.balance)}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                    </div>
                                </td>
                                <td data-label={t('col_date')}>
                                    <div className="creation-date">
                                        <FontAwesomeIcon icon={faCalendarAlt} /> {u.created_at ? new Date(u.created_at).toLocaleDateString('hy-AM') : '---'}
                                    </div>
                                </td>
                                <td data-label={t('col_status')}>
                                    <span className={`status-badge ${u.is_blocked ? 'status-blocked' : 'status-active'}`}>
                                        {u.is_blocked ? t('status_blocked') : t('status_verified')}
                                    </span>
                                </td>
                                <td data-label={t('col_actions')}>
                                    <div className="action-buttons-set">
                                        <button 
                                            className={`action-btn bypay ${u.can_pay_later ? 'active' : ''}`}
                                            onClick={() => handleToggleByPay(u.id, u.can_pay_later)}
                                            title={t('tooltip_bypay')}
                                        >
                                            <FontAwesomeIcon icon={faCheckDouble} />
                                        </button>
                                        <button 
                                            className={`action-btn block-toggle ${u.is_blocked ? 'unblock' : 'block'}`}
                                            onClick={() => handleToggleBlock(u.id, u.is_blocked)}
                                        >
                                            <FontAwesomeIcon icon={u.is_blocked ? faUnlock : faBan} />
                                        </button>
                                        <button className="action-btn view-profile">
                                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="6" className="empty-state">{t('no_users_found')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const StatBox = ({ icon, label, value, className }) => (
    <div className={`stat-card ${className}`}>
        <div className="stat-icon"><FontAwesomeIcon icon={icon} /></div>
        <div className="stat-content">
            <span className="stat-label">{label}</span>
            <span className="stat-value">{value}</span>
        </div>
    </div>
);

const modernUserStyles = `
    .users-page-container { padding: 30px; background: #f8faff; min-height: 100vh; font-family: 'Inter', sans-serif; }
    
    /* Header & Stats */
    .users-header-section { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; flex-wrap: wrap; gap: 20px; }
    .header-info h1 { font-size: 26px; color: #1e293b; margin: 0; font-weight: 800; }
    .header-info p { color: #64748b; margin: 5px 0 0; }
    
    .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 15px; flex-grow: 1; max-width: 800px; }
    .stat-card { padding: 15px 20px; border-radius: 16px; display: flex; align-items: center; gap: 15px; color: white; transition: 0.3s transform; cursor: default; }
    .stat-card:hover { transform: translateY(-5px); }
    .stat-icon { font-size: 20px; background: rgba(255,255,255,0.2); width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: 12px; }
    .stat-label { display: block; font-size: 11px; text-transform: uppercase; opacity: 0.9; font-weight: 600; }
    .stat-value { font-size: 18px; font-weight: 700; }
    
    .stat-blue { background: linear-gradient(135deg, #4f46e5, #7c3aed); }
    .stat-gold { background: linear-gradient(135deg, #f59e0b, #d97706); }
    .stat-green { background: linear-gradient(135deg, #10b981, #059669); }
    .stat-red { background: linear-gradient(135deg, #ef4444, #dc2626); }

    /* Toolbar */
    .users-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; gap: 15px; }
    .search-wrapper { position: relative; flex: 1; max-width: 400px; }
    .search-icon { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
    .search-wrapper input { width: 100%; padding: 12px 15px 12px 45px; border-radius: 12px; border: 1px solid #e2e8f0; outline: none; transition: 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.02); box-sizing: border-box;}
    .search-wrapper input:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1); }
    
    .refresh-action-btn { background: white; border: 1px solid #e2e8f0; padding: 10px 20px; border-radius: 12px; font-weight: 600; color: #475569; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: 0.2s; }
    .refresh-action-btn:hover { background: #f1f5f9; }
    .spinning svg { animation: spin 1s linear infinite; }
    @keyframes spin { from {transform: rotate(0deg);} to {transform: rotate(360deg);} }

    /* Table */
    .users-table-card { background: white; border-radius: 20px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); overflow: hidden; }
    .modern-users-table { width: 100%; border-collapse: collapse; text-align: left; }
    .modern-users-table th { background: #f8fafc; padding: 18px 20px; font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 700; border-bottom: 1px solid #f1f5f9; }
    .modern-users-table td { padding: 15px 20px; border-bottom: 1px solid #f1f5f9; }
    .row-blocked { background: #fffcfc; }
    
    /* Profile Cell */
    .user-profile-cell { display: flex; align-items: center; gap: 12px; }
    .avatar-wrapper { width: 45px; height: 45px; border-radius: 12px; overflow: hidden; background: #eef2ff; }
    .avatar-wrapper img { width: 100%; height: 100%; object-fit: cover; }
    .avatar-initials { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #4f46e5; color: white; font-weight: 700; }
    .user-info-text span { display: block; }
    .full-name { font-weight: 700; color: #1e293b; font-size: 14px; }
    .user-id { font-size: 11px; color: #94a3b8; margin-top: 2px; }

    /* Info Components */
    .contact-info .primary-phone { display: block; font-weight: 600; color: #475569; font-size: 13px; }
    .contact-info .secondary-phone { font-size: 11px; color: #94a3b8; }
    .amount { font-weight: 700; color: #4f46e5; margin-right: 15px; } /* Ավելացվել է տարածություն */
    .edit-balance-trigger { background: #f1f5f9; border: none; color: #4f46e5; cursor: pointer; font-size: 14px; padding: 5px; border-radius: 6px; }
    .edit-balance-trigger:hover { background: #e0e7ff; }
    .status-badge { padding: 5px 12px; border-radius: 8px; font-size: 11px; font-weight: 700; }
    .status-active { background: #dcfce7; color: #166534; }
    .status-blocked { background: #fee2e2; color: #991b1b; }

    /* Actions */
    .action-buttons-set { display: flex; gap: 8px; }
    .action-btn { width: 34px; height: 34px; border-radius: 10px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
    .bypay { background: #f1f5f9; color: #94a3b8; }
    .bypay.active { background: #e0e7ff; color: #4f46e5; }
    .block-toggle.block { background: #fff1f2; color: #e11d48; }
    .block-toggle.unblock { background: #f0fdf4; color: #22c55e; }
    .view-profile { background: #f8fafc; color: #64748b; }
    .action-btn:hover { transform: scale(1.1); }

    /* MOBILE VERSION (Responsive Grid) */
    @media (max-width: 900px) {
        .users-header-section { flex-direction: column; }
        .stats-grid { width: 100%; max-width: none; }
        
        .modern-users-table thead { display: none; }
        .modern-users-table tbody { 
            display: grid; 
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); 
            gap: 15px; 
            padding: 15px;
        }
        .modern-users-table tr { 
            display: flex; 
            flex-direction: column; 
            background: #fff; 
            border: 1px solid #e2e8f0; 
            border-radius: 20px; 
            padding: 0 15px 15px 15px; /* Վերևի padding-ը 0, որ գիծը կպնի */
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            overflow: hidden;
            position: relative;
        }

        /* Ոսկեգույն գիծը քարտի վերևում */
        .modern-users-table tr::before {
            content: "";
            display: block;
            height: 5px;
            width: calc(100% + 30px);
            margin-left: -15px;
            background: linear-gradient(90deg, #f59e0b, #d97706);
            margin-bottom: 10px;
        }

        .modern-users-table td { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            padding: 10px 0 !important; 
            border-bottom: 1px solid #f1f5f9 !important;
            width: 100%;
        }
        .modern-users-table td:last-child { border-bottom: none !important; padding-top: 15px !important; }
        .modern-users-table td::before { 
            content: attr(data-label); 
            font-size: 10px; 
            font-weight: 700; 
            color: #94a3b8; 
            text-transform: uppercase;
        }
        .action-buttons-set { width: 100%; justify-content: space-around; }
        .action-btn { flex: 1; height: 40px; }
    }

    @media (max-width: 480px) {
        .modern-users-table tbody { grid-template-columns: 1fr; }
        .stats-grid { grid-template-columns: 1fr 1fr; }
    }
`;

export default AdminUsers;